'use client'

import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { cn } from '@/ui/utils/style'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { useStableLoadedData } from '../logic/useStableLoadedData'
import { getTopEarningOpportunities } from '../logic/useUnifiedEarnFilteredOpportunities'
import { useSharedUnifiedEarnOpportunities } from '../logic/useUnifiedEarnOpportunities'
import type { YieldOpportunity } from '../types'
import { getYieldOpportunityDetailsPath } from '../utils/getYieldOpportunityDetailsPath'
import { TopEarningSection } from './TopEarningSection'

export function FeaturedOpportunities() {
  const router = useRouter()
  const { opportunities, isLoading } = useSharedUnifiedEarnOpportunities()
  const { data: displayedOpportunities, hasLoaded } = useStableLoadedData(opportunities, isLoading)
  const topEarning = useMemo(() => getTopEarningOpportunities(displayedOpportunities, 4), [displayedOpportunities])

  const handleOpportunityClick = useCallback(
    (opportunity: YieldOpportunity) => {
      router.push(getYieldOpportunityDetailsPath(opportunity))
    },
    [router],
  )

  const showLoadingState = isLoading && !hasLoaded

  if (showLoadingState) return <FeaturedCardsSkeleton />
  if (topEarning.length === 0) return null

  return <TopEarningSection opportunities={topEarning} onOpportunityClick={handleOpportunityClick} />
}

function FeaturedCardsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-32" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              'h-32 rounded-xl border border-white/[0.04] bg-[#111114] p-4 sm:h-40',
              i > 2 && 'hidden sm:block',
            )}
          >
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
